import _ from 'underscore';
import classNames from 'classnames';
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { OverflowSet } from '@fluentui/react/lib/OverflowSet';
import { IconButton, CommandBarButton } from '@fluentui/react/lib/Button';

import { tileHeaderConfigShape } from './shared-props';
import { getStyles } from './get-styles';

const getDropdownItems = actions => _.map(actions, ({ text, callback, disabled }) => ({
  key: text,
  name: text,
  text,
  onClick: callback,
  disabled,
}));

export const actionComponentTypes = {
  OverflowSet: 'OverflowSet',
  DropdownButton: 'DropdownButton',
};

export const TileHeaderUnstyled = (props) => {
  const {
    classes,
    config: {
      renderHeaderText = () => null,
      helpIcon,
      isRequired,
      actions: headerOptions = [],
      onCloseTile,
      i18n,
      isLargeHeader = false,
      as: Component = 'h3',
      moreOptionsLabel,
      actionComponentType = actionComponentTypes.OverflowSet,
    },
    classPrefix = '',
  } = props;

  const hasHeaderOptions = !_.isEmpty(headerOptions);
  const items = getDropdownItems(headerOptions);

  const onRenderOverflowButton = () => {
    const buttonStyles = {
      root: {
        minWidth: 0,
        padding: '0 4px',
        alignSelf: 'stretch',
        height: 'auto',
      },
    };
    return (
      <IconButton
        title={i18n.getString(_TL_('More options'))}
        styles={buttonStyles}
        menuIconProps={{ iconName: 'More' }}
        menuProps={{ items }}
        aria-label={moreOptionsLabel}
      />
    );
  };

  const ActionOverflowSet = useCallback(() => (
    <OverflowSet
      items={[]}
      overflowItems={items}
      onRenderOverflowButton={() => onRenderOverflowButton()}
    />
  ), [items, onRenderOverflowButton]);

  const [selectedAction, setSelectedAction] = useState(hasHeaderOptions ? items[0].text : null);

  const ActionDropdownButton = useCallback(() => {
    const newItems = items.map(item => ({
      ...item,
      onClick: (...args) => {
        item.onClick(...args);
        setSelectedAction(item.text);
      },
    }));

    return (<CommandBarButton
      text={selectedAction}
      menuProps={{ items: newItems }}
    />);
  }, [items, selectedAction]);

  useEffect(() => {
    if (!_.isEmpty(items) && actionComponentType === actionComponentTypes.DropdownButton) {
      setSelectedAction(items[0].text);
    }
  }, [actionComponentType]);

  useEffect(() => {
    if (!_.isEmpty(items) &&
      actionComponentType === actionComponentTypes.DropdownButton &&
      _.isFunction(items[0].onClick)) {
      items[0].onClick();
    }
  }, [actionComponentType]);

  if (isRequired !== true) { return null; }

  const headerClasses = classNames(
    `${classPrefix}tile-header-text`,
    classes.headerText,
    isLargeHeader ? classes.largeHeaderText : classes.smallHeaderText
  );

  return (
    <div className={classNames(classes.headerContainer, `${classPrefix}tile-header-container`, 'tile-header-container')}>
      <Component className={headerClasses}>
        {renderHeaderText()}
      </Component>
      <div className={classNames(`${classPrefix}tile-header-help`, classes.headerHelp, 'tile-header-help')}>
        {helpIcon}
      </div>
      {hasHeaderOptions ? (
        <div className={classNames(`${classPrefix}dropdown-container`, classes.dropdownContainer, 'dropdown-container')}>
          {actionComponentType === actionComponentTypes.OverflowSet && (<ActionOverflowSet />)}
          {actionComponentType === actionComponentTypes.DropdownButton && (<ActionDropdownButton />)}
        </div>
      ) : null}
      {onCloseTile &&
        <IconButton
          className={classes.closeButton}
          onClick={onCloseTile}
          iconProps={{ iconName: 'Cancel' }}
        />
      }
    </div>
  );
};

TileHeaderUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(getStyles(), _.constant(PropTypes.string.isRequired)))
    .isRequired,
  config: tileHeaderConfigShape,
  classPrefix: PropTypes.string,
};

TileHeaderUnstyled.defaultProps = {
  config: {
    isRequired: false,
    actions: [],
    onCloseTile: undefined,
    renderHeaderText: () => null,
    helpIcon: null,
    moreOptionsLabel: null,
    actionComponentType: actionComponentTypes.OverflowSet,
  },
  classPrefix: '',
};

export const TileHeader = withDefaultStyles(
  TileHeaderUnstyled,
  getStyles
);
